import { FreightDetail } from 'src/app/models/order.model';

export enum LengthUOM {
  Inches = "IN",
  Feet = "FT"
}

export class QuoteItem {
  public _lengthInput: string = "";
  public _widthInput: string = "";
  public _heightInput: string = "";
  public _weightInput: string = "";

  public _quantity: number = 1;
  public _length: number;
  public _width: number;
  public _height: number;
  public _weight: number;
  public _cubicFeet: number;
  public _lengthUOM: LengthUOM = LengthUOM.Inches;

  description: string;

  set quantity(v: number) {
    this._quantity = v;
    this.recalculateCubicFeet();
  }
  get quantity() { return this._quantity; }

  set lengthInput(v: string) {
    this._lengthInput = v;
    var number = this.convertUnitsByInput(v);
    if (!Number.isNaN(number)) {
      this.length = number;
    }
  }
  
  setLengthWithUnit(value: number, unit: LengthUOM) {
    this._length = this.convertToLengthUnit(value, unit)
  }

  setWidthWithUnit(value: number, unit: LengthUOM) {
    this._width = this.convertToLengthUnit(value, unit)
  }

  setHeightWithUnit(value: number, unit: LengthUOM) {
    this._height = this.convertToLengthUnit(value, unit)
  }
  
  setWeightWithUnit(value: number, unit: 'kg' | string) {
    if (unit == 'kg') {
      this._weight = QuoteItem.convertKgToLb(value);
    } else if (unit === 'lb' || unit === 'lbs.') {
      this._weight = QuoteItem.convertKgToLb(value);
    } else if (!!unit) {
      console.warn(`unknown unit ${unit}, defaulting to lb`)
      this._weight = value;
    } else {
      this._weight = value;
    }
  }
  
  set length(v: number) {
    this._length = v;
    this.recalculateCubicFeet();
  }
  get lengthInput(): string { return this._lengthInput; }
  get length(): number { return this._length; }

  set widthInput(v: string) {
    this._widthInput = v;
    var number = this.convertUnitsByInput(v);
    if (!Number.isNaN(number)) {
      this.width = number;
    }
  }
  set width(v: number) {
    this._width = v;
    this.recalculateCubicFeet();
  }
  get widthInput(): string { return this._widthInput; }
  get width(): number { return this._width; }

  set heightInput(v: string) {
    this._heightInput = v;
    var number = this.convertUnitsByInput(v);
    if (!Number.isNaN(number)) {
      this.height = number;
    }
  }
  set height(v: number) {
    this._height = v;
    this.recalculateCubicFeet();
  }
  get heightInput(): string { return this._heightInput; }
  get height(): number { return this._height; }

  set weightInput(v: string) {
    this._weightInput = v;
    var number = this.convertUnitsByInput(v);
    if (!Number.isNaN(number)) {
      this.weight = number;
    }
  }
  set weight(v: number) {
    this._weight = v;
  }
  get weightInput(): string { return this._weightInput; }
  get weight(): number { return this._weight; }

  set lengthUOM(v: LengthUOM) {
    var conversionFactor: number = 1;
    if (this._lengthUOM == LengthUOM.Feet && v == LengthUOM.Inches) {
      conversionFactor = 12;
    } else if (this._lengthUOM == LengthUOM.Inches && v == LengthUOM.Feet) {
      conversionFactor = 1/12;
    }
    this.length = this.length * conversionFactor;
    this.width = this.width * conversionFactor;
    this.height = this.height * conversionFactor;
    this._lengthUOM = v;

    this.recalculateCubicFeet();
    this.cleanUpFormatStrings();
  }
  get lengthUOM() { return this._lengthUOM; }

  get cubicFeet() {
    return this._cubicFeet;
  }

  toFreightDetail(): FreightDetail {
    let item = new FreightDetail();

    item.description = this.description;

    item.pieces = this.quantity;
    item.pieceUnit = 'PC';

    item.weight = this.weight;
    item.weightUnit = 'LB';

    item.length = this.length;
    item.lengthUnit = this.lengthUOM;
    item.height = this.height;
    item.heightUnit = this.lengthUOM;
    item.width = this.width;
    item.widthUnit = this.lengthUOM;

    return item;
  }

  get formattedVolume() {
    return this.formatQuantity(this.quantity * this.length * this.width * this.height);
  }

  get formattedHeight() {
    return this.formatQuantity(this.height);
  }

  get formattedWidth() {
    return this.formatQuantity(this.width);
  }

  get formattedLength() {
    return this.formatQuantity(this.length);
  }

  recalculateCubicFeet() {
    const conversionFactor = this.lengthUOM == "IN" ? 12 : 1;
    this._cubicFeet = this.quantity * this.length * this.width * this.height / Math.pow(conversionFactor, 3);
  }

  cleanUpFormatStrings() {
    this._lengthInput = this.formatQuantity(this.length);
    this._widthInput = this.formatQuantity(this.width);
    this._heightInput = this.formatQuantity(this.height);
    this._weightInput = this.formatWeight(this.weight);
  }

  convertToLengthUnit(value: number, unit: LengthUOM) {
    if (unit == this.lengthUOM) {
      return value;
    } else if (unit == LengthUOM.Feet && this.lengthUOM == LengthUOM.Inches) {
      return value * 12;
    } else {
      // unit must be inches, this.lengthUOM must be feet
      return value / 12;
    }
  }

  convertUnitsByInput(input: string): number {
    if (input.match(/\s*(ft|ft.|')\s*$/i)) {
      return this.convertToLengthUnit(parseFloat(input), LengthUOM.Feet)
    } else if (input.match(/\s*(in|")\s*$/i)) {
      return this.convertToLengthUnit(parseFloat(input), LengthUOM.Inches)
    } else if (input.match(/\s*(kg|kg.)\s*$/i)) {
      return QuoteItem.convertKgToLb(parseFloat(input))
    } else {
      return parseFloat(input);
    }
  }

  formatQuantity(value: number): string {
    if (!value) {
      return '';
    }

    var uomString: string;
    switch (this.lengthUOM) {
      case LengthUOM.Inches: 
        uomString = ' in.';
        break;
      case LengthUOM.Feet:
        uomString = " ft.";
        break;
    }
    return value.toLocaleString(undefined, {maximumFractionDigits: 3}) + uomString;
  }

  formatWeight(value: number): string {
    if (!value) {
      return '';
    }

    return value.toLocaleString(undefined, {maximumFractionDigits: 2}) + ' lbs.';
  }
  
  static convertKgToLb(value: number) {
    return value * 2.20462262;
  }

  constructor(lengthUOM: LengthUOM) {
    this.lengthUOM = lengthUOM;
  }
}
